import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Percent, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { formatSek } from '../constants';

/**
 * Utdelningsguiden — 3:12-skatteeffekten VID beslutet, som ett kompakt
 * kapacitetsmått i husets formspråk (modern-card-kemi: tintad ikonchip,
 * statchips med färgprick + etikett — aldrig färg ensam, tunn mätare med
 * rundade ändar och 2px ytmellanrum mellan segmenten, text i texttoner).
 * Ren vägledning: aldrig en spärr. Backend: suggestions.utdelningsSkatt.
 *
 * @param {object} guide - suggestions.utdelningsSkatt
 * @param {number} utdelning - föreslagen utdelning (0 när fältet är tomt)
 * @param {number} till - summan till förfogande (beloppsspärrens tak)
 * @param {string} companyId
 * @param {(amount: number) => void} onApplyOptimal
 */
const UtdelningsGuide = ({ guide, utdelning, till, companyId, onApplyOptimal }) => {
    const [showDetails, setShowDetails] = useState(false);
    if (!guide) return null;

    const styles = (
        <style>{`
            .ye-ug {
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                background: #f9fafb;
                padding: 0.9rem 1rem;
                margin: 0.9rem 0 0.5rem;
                max-width: 640px;
                font-size: 0.8125rem;
                color: #374151;
            }
            .ye-ug-head { display: flex; align-items: center; gap: 0.6rem; }
            .ye-ug-icon {
                flex-shrink: 0; width: 30px; height: 30px; border-radius: 8px;
                background: var(--rgba-primary-1); color: var(--primary);
                display: inline-flex; align-items: center; justify-content: center;
            }
            .ye-ug-title { font-weight: 600; color: #111827; font-size: 0.875rem; }
            .ye-ug-badge {
                margin-left: auto; font-size: 0.6875rem; color: #6b7280;
                background: #fff; border: 1px solid #e5e7eb; border-radius: 999px;
                padding: 0.15rem 0.55rem; white-space: nowrap;
            }
            .ye-ug-chips { display: flex; flex-wrap: wrap; gap: 1.25rem; margin: 0.75rem 0 0.6rem; }
            .ye-ug-chip { display: flex; align-items: baseline; gap: 0.4rem; }
            .ye-ug-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; align-self: center; }
            .ye-ug-dot-20 { background: var(--primary); }
            .ye-ug-dot-lon { background: #f59e0b; }
            .ye-ug-chip-label { color: #6b7280; }
            .ye-ug-chip-value { font-weight: 600; color: #111827; font-variant-numeric: tabular-nums; }
            .ye-ug-bar-wrap { position: relative; margin: 0.25rem 0 0.2rem; }
            .ye-ug-bar {
                display: flex; height: 12px; border-radius: 6px; overflow: hidden;
                background: #e5e7eb;
            }
            .ye-ug-seg-20 { background: var(--primary); }
            .ye-ug-seg-lon { background: #f59e0b; }
            .ye-ug-gap { width: 2px; background: #f9fafb; flex-shrink: 0; }
            .ye-ug-tick {
                position: absolute; top: -3px; bottom: -3px; width: 2px;
                background: #374151; border-radius: 1px;
            }
            .ye-ug-scale {
                display: flex; justify-content: space-between;
                font-size: 0.6875rem; color: #9ca3af; margin-top: 0.25rem;
                font-variant-numeric: tabular-nums;
            }
            .ye-ug-note { margin-top: 0.5rem; color: #6b7280; }
            .ye-ug-actions { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.65rem; flex-wrap: wrap; }
            .ye-ug-details-btn {
                border: none; background: none; padding: 0; color: #6b7280;
                font-size: 0.75rem; display: inline-flex; align-items: center; gap: 2px;
                cursor: pointer;
            }
            .ye-ug-details-btn:hover { color: #374151; }
            .ye-ug-details { margin-top: 0.5rem; border-top: 1px dashed #e5e7eb; padding-top: 0.5rem; color: #6b7280; }
            .ye-ug-details div + div { margin-top: 0.3rem; }
        `}</style>
    );

    // ── Saknade ägarprofiler: kompakt CTA i stället för tystnad ────────────
    if (guide.unavailable) {
        if (!guide.needsProfiles) return null;
        return (
            <div className="ye-ug">
                {styles}
                <div className="ye-ug-head">
                    <span className="ye-ug-icon"><Percent size={15} /></span>
                    <span className="ye-ug-title">Hur beskattas utdelningen?</span>
                </div>
                <div className="ye-ug-note" style={{ marginTop: '0.6rem' }}>
                    Utdelning inom ditt <strong>gränsbelopp</strong> beskattas med bara{' '}
                    <strong>20 %</strong> — resten som lön (upp till ca 55 %). Fyll i
                    ägaruppgifterna så räknar vi ut ditt utrymme och visar det här.
                </div>
                <div className="ye-ug-actions">
                    <Link className="btn btn-outline-primary btn-sm" to={`/${companyId}/tax-planning`}>
                        Fyll i ägaruppgifter i Skatteplaneringen
                    </Link>
                </div>
            </div>
        );
    }

    const grans = Math.max(0, Number(guide.totalGransbelopp) || 0);
    const utd = Math.max(0, Number(utdelning) || 0);
    const over = Math.max(0, utd - grans);
    const inom = Math.min(utd, grans);
    const kvarInom20 = Math.max(0, Math.min(grans, till) - utd);
    // Optimalt = hela 20 %-utrymmet, men aldrig över fritt eget kapital
    // (beloppsspärren, ABL 17:3).
    const optimal = Math.round(Math.min(grans, Math.max(0, till)));
    const optimalBegransadAvEK = grans > till;

    // Mätarskala: gränsbeloppet, eller utdelningen när den överstiger det.
    const scale = Math.max(grans, utd, 1);
    const pct = (v) => `${Math.max(0, Math.min(100, (v / scale) * 100))}%`;

    return (
        <div className="ye-ug">
            {styles}
            <div className="ye-ug-head">
                <span className="ye-ug-icon"><Percent size={15} /></span>
                <span className="ye-ug-title">Skatt på utdelningen</span>
                <span className="ye-ug-badge">
                    {guide.rules === 'new_2026' ? 'nya 3:12-reglerna' : 'äldre 3:12-regler'}
                    {' · '}{guide.beskattningsar}
                </span>
            </div>

            <div className="ye-ug-chips">
                <span className="ye-ug-chip">
                    <span className="ye-ug-dot ye-ug-dot-20" aria-hidden="true" />
                    <span className="ye-ug-chip-label">20 % skatt (inom gränsbeloppet)</span>
                    <span className="ye-ug-chip-value">{formatSek(utd > 0 ? inom : grans)}</span>
                </span>
                {over > 0 && (
                    <span className="ye-ug-chip">
                        <span className="ye-ug-dot ye-ug-dot-lon" aria-hidden="true" />
                        <span className="ye-ug-chip-label">beskattas som lön (upp till ca 55 %)</span>
                        <span className="ye-ug-chip-value">{formatSek(over)}</span>
                    </span>
                )}
            </div>

            <div
                className="ye-ug-bar-wrap"
                role="img"
                aria-label={utd > 0
                    ? `Av utdelningen ${formatSek(utd)} beskattas ${formatSek(inom)} med 20 procent och ${formatSek(over)} som lön`
                    : `Utrymme för 20 procents skatt: ${formatSek(grans)}`}
            >
                <div className="ye-ug-bar">
                    <div className="ye-ug-seg-20" style={{ width: pct(utd > 0 ? inom : grans) }} />
                    {over > 0 && <div className="ye-ug-gap" />}
                    {over > 0 && <div className="ye-ug-seg-lon" style={{ width: pct(over) }} />}
                </div>
                {over > 0 && <div className="ye-ug-tick" style={{ left: pct(grans) }} aria-hidden="true" />}
            </div>
            <div className="ye-ug-scale">
                <span>0 kr</span>
                <span>gränsbelopp {formatSek(grans)}</span>
                {over > 0 && <span>{formatSek(utd)}</span>}
            </div>

            {utd > 0 && over === 0 && kvarInom20 > 0 && (
                <div className="ye-ug-note">
                    Hela beloppet ryms inom 20 %-utrymmet. {formatSek(kvarInom20)} av utrymmet
                    är kvar — outnyttjat gränsbelopp sparas till kommande år.
                </div>
            )}
            {utd === 0 && (
                <div className="ye-ug-note">
                    Upp till {formatSek(grans)} kan delas ut med 20 % skatt. Outnyttjat
                    utrymme sparas till kommande år.
                </div>
            )}

            <div className="ye-ug-actions">
                {optimal > 0 && Math.round(utd) !== optimal && onApplyOptimal && (
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => onApplyOptimal(optimal)}
                    >
                        <Sparkles size={14} />
                        Använd {formatSek(optimal)}
                        {optimalBegransadAvEK ? ' (max inom fritt eget kapital)' : ' (hela 20 %-utrymmet)'}
                    </button>
                )}
                <button
                    type="button"
                    className="ye-ug-details-btn"
                    onClick={() => setShowDetails((v) => !v)}
                    aria-expanded={showDetails}
                >
                    {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Antaganden{guide.owners?.length > 1 ? ' & per ägare' : ''}
                </button>
            </div>

            {showDetails && (
                <div className="ye-ug-details">
                    {guide.owners?.length > 1 && guide.owners.map((o) => (
                        <div key={o.name}>
                            {o.name} ({o.sharePct} %): gränsbelopp {formatSek(o.gransbelopp)}
                        </div>
                    ))}
                    {(guide.caveats || []).map((c) => <div key={c}>{c}</div>)}
                    <div>
                        Beräknat med samma regler som{' '}
                        <Link to={`/${companyId}/tax-planning`}>Skatteplaneringen</Link> —
                        detaljer och antaganden finns där.
                    </div>
                </div>
            )}
        </div>
    );
};

export default UtdelningsGuide;
